/**
 * Pieces shared by the goal and liability cards of the on-screen report:
 * the every-rupee trace, the fund basket, segment pills, fact grids and
 * callouts. Column sets match the PDF so the two never disagree.
 */

import React from "react";
import {
  SCORE_FACTOR_LABELS,
  percent,
  reportDate,
  rupee,
  rupees,
} from "../../../Helpers/portfolioPlanner/format";
import type {
  PlannerEligibleBond,
  PlannerFund,
  PlannerSegment,
  PlannerTraceRow,
} from "../../../Helpers/portfolioPlanner/types";
import styles from "./portfolio-planner.module.css";

export const SegmentPill = ({ segment }: { segment: PlannerSegment }) => (
  <span
    className={`${styles.pill} ${
      segment === "Equity"
        ? styles.pillEquity
        : segment === "Hybrid"
          ? styles.pillHybrid
          : styles.pillDebt
    }`}
  >
    {segment}
  </span>
);

export const Callout = ({
  tone = "neutral",
  children,
}: {
  tone?: "neutral" | "good" | "warn" | "bad";
  children: React.ReactNode;
}) => (
  <p
    className={`${styles.callout} ${
      tone === "good"
        ? styles.calloutGood
        : tone === "warn"
          ? styles.calloutWarn
          : tone === "bad"
            ? styles.calloutBad
            : ""
    }`}
  >
    {children}
  </p>
);

export const FactGrid = ({
  facts,
}: {
  facts: { label: string; value: string }[];
}) => (
  <div className={styles.facts}>
    {facts.map((fact) => (
      <div key={fact.label} className={styles.fact}>
        <span>{fact.label}</span>
        <b>{fact.value}</b>
      </div>
    ))}
  </div>
);

/** The workings, in order — the report's whole point. */
export const TraceTable = ({
  trace,
  caption = "Every rupee — step, basis and amount",
}: {
  trace: PlannerTraceRow[];
  caption?: string;
}) => (
  <div className={styles.tableWrap}>
    <table className={styles.table}>
      <caption>{caption}</caption>
      <thead>
        <tr>
          <th>Step</th>
          <th style={{ textAlign: "right" }}>Amount</th>
        </tr>
      </thead>
      <tbody>
        {trace.map((row, index) => (
          <tr key={`${row.label}-${index}`}>
            <td>
              {row.label}
              <i className={styles.formula}>{row.formula}</i>
            </td>
            <td className={`${styles.numeric} ${styles.strong}`}>
              {row.value == null ? "" : rupee(row.value)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/**
 * Names the factors a fund had no data for, which counted as average. Plans
 * generated before the six-factor score carry no breakdown, so for those a
 * basis shorter than the old four factors is the only signal.
 */
const thinDataNote = (fund: PlannerFund): string => {
  if (fund.scoreFactors) {
    const missing = Object.keys(SCORE_FACTOR_LABELS).filter(
      (key) => fund.scoreFactors?.[key]?.status === "missing"
    );
    return missing.length
      ? ` · no ${missing.map((key) => SCORE_FACTOR_LABELS[key]).join(", ")} data, counted as average`
      : "";
  }
  return fund.scoreBasis?.length > 0 && fund.scoreBasis.length < 4
    ? ` · scored on ${fund.scoreBasis.join(", ")} only`
    : "";
};

/**
 * The recommended basket. A factor with no data counts as average rather than
 * being dropped, and the row says which ones did, so a score resting on gaps
 * is never shown as if it were complete.
 */
export const FundsTable = ({
  funds,
  frequency,
  caption,
}: {
  funds: PlannerFund[];
  frequency: string;
  caption: string;
}) => (
  <div className={styles.tableWrap}>
    <table className={styles.table}>
      <caption>{caption}</caption>
      <thead>
        <tr>
          <th>Scheme</th>
          <th>Family</th>
          <th style={{ textAlign: "right" }}>SMEST score</th>
          <th style={{ textAlign: "right" }}>&#945;</th>
          <th style={{ textAlign: "right" }}>&#946;</th>
          <th style={{ textAlign: "right" }}>Std dev</th>
          <th style={{ textAlign: "right" }}>Expense</th>
          <th style={{ textAlign: "right" }}>{frequency}</th>
        </tr>
      </thead>
      <tbody>
        {funds.map((fund) => (
          <tr key={`${fund.isin}-${fund.name}`}>
            <td>
              <span className={styles.strong}>{fund.name}</span>
              {fund.prime ? <i className={styles.primeTag}>PRIME</i> : null}
              <i className={styles.formula}>
                {fund.amc}
                {thinDataNote(fund)}
              </i>
            </td>
            <td className={styles.subtle}>{fund.family}</td>
            <td className={styles.numeric}>
              {fund.score == null ? (
                <span className={styles.subtle}>not scored</span>
              ) : (
                <>
                  <i className={styles.scoreBar}>
                    <i style={{ width: `${Math.min(100, fund.score * 10)}%` }} />
                  </i>
                  {fund.score.toFixed(1)}
                </>
              )}
            </td>
            <td className={styles.numeric}>
              {fund.alpha == null ? "—" : fund.alpha.toFixed(2)}
            </td>
            <td className={styles.numeric}>
              {fund.beta == null ? "—" : fund.beta.toFixed(2)}
            </td>
            <td className={styles.numeric}>
              {fund.std == null ? "—" : fund.std.toFixed(2)}
            </td>
            <td className={styles.numeric}>
              {fund.expense == null ? "—" : percent(fund.expense, 2)}
            </td>
            <td className={`${styles.numeric} ${styles.strong}`}>
              {rupees(fund.amount)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/** β here is a peer-relative volatility proxy, not a regression beta. */
export const FundsFootnote = () => (
  <p className={styles.disclaimer}>
    SMEST score runs 0–10 from alpha, standard deviation, P/E (not for debt
    funds), expense ratio, annualised returns and beta, each ranked against peer
    funds. A factor with no data counts as average. β is a peer-relative
    volatility measure, not a regression beta against an index. PRIME marks
    funds on the hand-picked list.
  </p>
);

/**
 * The bonds that qualified for this goal.
 *
 * Shown even when only one did. The spec treats "one eligible" and "best of
 * several" as different states with different copy, not as a chooser with
 * fewer options — with a single candidate this is the selection and its
 * evidence, and the generated caveat above it already says it was chosen
 * without competition. Listing it still answers the question a reader
 * actually has: what else was on the table.
 */
export const EligibleBondsTable = ({
  bonds,
  lumpsum,
}: {
  bonds: PlannerEligibleBond[];
  lumpsum: number;
}) => {
  if (!bonds.length) return null;
  const many = bonds.length > 1;

  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <caption>
          {many
            ? `Applicable bonds — ${bonds.length} qualified for this goal date`
            : "Applicable bonds — the only one that qualified"}
        </caption>
        <thead>
          <tr>
            <th>Bond</th>
            <th style={{ textAlign: "right" }}>Coupon</th>
            <th style={{ textAlign: "right" }}>YTM</th>
            <th>Maturity</th>
            <th style={{ textAlign: "right" }}>Settles at</th>
            <th style={{ textAlign: "right" }}>Minimum</th>
          </tr>
        </thead>
        <tbody>
          {bonds.map((bond) => (
            <tr key={bond.isin}>
              <td>
                <span className={styles.strong}>{bond.name}</span>
                {bond.selected ? (
                  <i className={styles.primeTag}>SELECTED</i>
                ) : null}
                <i className={styles.formula}>
                  {bond.rating || "no rating returned"} · {bond.isin}
                  {/* Whether it repays before the money is needed is the single
                      most useful thing about a candidate, so it sits here
                      rather than in a column nobody scans. */}
                  {bond.maturesBeforeGoal === false && bond.gapDays
                    ? ` · matures ${bond.gapDays} days after the goal`
                    : ""}
                </i>
              </td>
              <td className={styles.numeric}>
                {bond.coupon == null ? "—" : percent(bond.coupon, 2)}
                {bond.freq ? <i className={styles.formula}>{bond.freq}</i> : null}
              </td>
              <td className={`${styles.numeric} ${styles.strong}`}>
                {bond.ytm == null ? "—" : percent(bond.ytm, 2)}
              </td>
              <td className={styles.subtle}>{reportDate(bond.maturity)}</td>
              <td className={styles.numeric}>
                {bond.settlementAmount == null
                  ? "—"
                  : rupees(bond.settlementAmount)}
              </td>
              <td
                className={`${styles.numeric} ${
                  (bond.minimumInvestment ?? 0) > lumpsum ? styles.negative : ""
                }`}
              >
                {bond.minimumInvestment == null
                  ? "—"
                  : rupees(bond.minimumInvestment)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
